// covered-by: frontends/carrie/src/routes/settings/__tests__/DemoResetCard.test.tsx
import { Button, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
// eslint-disable-next-line import/named
import { createRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { DemoBrandArtifactsCard } from "./components/DemoBrandArtifactsCard";
import { DemoBrandConfig } from "./components/DemoBrandConfig";
import { DemoImpersonationCard } from "./components/DemoImpersonationCard";
import { DemoKillerFlowCard } from "./components/DemoKillerFlowCard";
import { DemoPrerunCard } from "./components/DemoPrerunCard";
import { DemoReviewFunctionsCard } from "./components/DemoReviewFunctionsCard";
import { DemoScriptedResponsesCard } from "./components/DemoScriptedResponsesCard";

import {
  bootstrapDemoEnvironment,
  getDemoResetJobStatus,
  resetDemoEnvironment,
  seedDemoChats,
} from "../../services/demo";
import {
  isDemoEnterprise,
  isMeaningfulGigsOrDemo,
  isMeaningfulGigsRole,
} from "../../utils/auth";
import { useAppDispatch } from "../../utils/hooks";
import { errorMessage } from "../../utils/http";
import { askPunttKeys } from "../../utils/queries/askPuntt";
import { setDemoPersona } from "../../utils/slices/auth";
import { authLayoutRoute } from "../auth-layout/route";

export const settingsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "settings",

  component: Settings,
});

function Settings() {
  const showDemoCard = isMeaningfulGigsOrDemo();
  // Bootstrap card: MG staff sitting on a real tenant. They need this
  // exactly when the demo enterprise doesn't exist yet on the stage —
  // i.e. the migrator hasn't been invoked. We surface it to any MG user
  // whose active enterprise isn't itself the demo (the demo Reset card
  // covers that case).
  //
  // Previously gated on `MODE !== "production"` so the bootstrap
  // affordance never appeared on the prod Carrie build. Ronnie asked
  // for the demo to be runnable against `app.puntt.ai` directly, so
  // the prod build now exposes the same card. The matching backend
  // gates in christo (`bootstrapDemoEnvironment`) and prince
  // (`workOsLogin` demo bypass) were dropped at the same time —
  // search "Ronnie's request" across those handlers for the
  // matching policy comments.
  const showBootstrapCard = isMeaningfulGigsRole() && !isDemoEnterprise();

  // Knowledge / Integration settings entry points used to live here as
  // duplicate cards. They're already accessible from the sidebar gear
  // dropdown (`sidebar-settings-knowledge`,
  // `sidebar-settings-integration`), so we removed the cards to keep
  // the Settings page focused on the demo-tenant controls that don't
  // have another entry point.

  // Outer wrapper bounds the article to its grid cell
  // (`flex h-full flex-col overflow-hidden`). The article itself uses
  // PLAIN BLOCK FLOW (no `display: grid`, no `display: flex`) and just
  // scrolls vertically when content overflows. Cards stack as block
  // children at their natural heights via `space-y-6`. Anything
  // fancier (grid auto-rows, flex column with shrink) is what causes
  // the recurring "all cards equal-height + clipped" regression on
  // shorter viewports.
  return (
    <div
      data-testid="settings-page"
      className="flex h-full flex-col overflow-hidden"
    >
      <article className="flex-1 space-y-6 overflow-y-auto px-10 py-6">
        <header className="flex items-center justify-between">
          <Heading as="h1" size="7" weight="bold">
            Settings
          </Heading>
        </header>
        {/*
          Card headings below all set as="h2" — Radix Heading defaults
          to <h1> when `as` is omitted, which produced 5 h1's on this
          page and tripped Playwright strict-mode in
          qa/rickrubin/tests/specs/cca/settings.navigation.spec.ts.
          Keeping the page-title h1 unique also matches a11y best
          practice (single h1 per route).
        */}

        {/*
          Anchor target for the AppSidebar's "Demo Settings" dropdown
          link (id="demo-settings"). Clicking the sidebar entry sets
          the URL hash to #demo-settings and the browser scrolls this
          card into view. Wrapping ALL demo-tenant cards in the same
          anchored div keeps the scroll target stable even when the
          mix of cards changes (e.g. DemoBootstrapCard is hidden on
          the demo tenant). Empty for real customer tenants — they
          never see the dropdown entry, so the anchor never resolves
          and stays inert.
        */}
        {/*
          Card order follows the real demoer's workflow rather than
          historic "what got added when". Top of the page is what you
          do BEFORE a meeting (env setup, personalize, configure
          reviewers, upload buyer files, write custom answers), the
          middle is the trigger you use DURING the meeting, and the
          bottom is the cleanup you do AFTER (reset). DemoBootstrap is
          first when it's visible because a non-demo enterprise can't
          do anything else until the demo company exists.
        */}
        <div id="demo-settings" className="scroll-mt-6 space-y-6">
          {/* Setup (rare; only before the demo company exists). */}
          {showBootstrapCard ? <DemoBootstrapCard /> : null}
          {/* Personalize for the buyer. */}
          {isDemoEnterprise() ? <DemoBrandConfig /> : null}
          {isDemoEnterprise() ? <DemoImpersonationCard /> : null}
          {/* Configure what the AI does. */}
          {isDemoEnterprise() ? <DemoReviewFunctionsCard /> : null}
          {/* Upload the buyer's files for the AI to use / pre-review. */}
          {isDemoEnterprise() ? <DemoBrandArtifactsCard /> : null}
          {isDemoEnterprise() ? <DemoPrerunCard /> : null}
          {/* Custom Q&A overrides (optional pre-demo polish). */}
          {isDemoEnterprise() ? <DemoScriptedResponsesCard /> : null}
          {/* In-meeting trigger. */}
          {isDemoEnterprise() ? <DemoKillerFlowCard /> : null}
          {/* After-the-meeting cleanup. */}
          {showDemoCard ? <DemoResetCard /> : null}
        </div>
      </article>
    </div>
  );
}

/**
 * Async polling cadence for the Reset Demo job status. Reset wipes ~15
 * collections + re-seeds + triggers guidelines ingestion — usually
 * 30-60s end-to-end. 2s polls give the operator a smooth progress
 * UX without hammering the lambda. Hard ceiling at 5 minutes
 * (DEMO_RESET_POLL_TIMEOUT_MS) so a hung consumer never spins the UI
 * forever; we then surface a "still running, check back later"
 * message rather than claim success or failure.
 */
const DEMO_RESET_POLL_INTERVAL_MS = 2000;
const DEMO_RESET_POLL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * One-click "Reset Demo Environment" card. MG-only — the backend handler
 * also refuses unless the active enterprise has Enterprise.isDemo=true,
 * so a misconfigured stage cannot wipe real customer data.
 *
 * Async flow:
 *   1. POST /demo/reset returns 202 + jobId immediately (well inside
 *      API Gateway's 29s sync limit).
 *   2. We poll GET /demo/reset/status/:jobId every 2s until the job
 *      lands in a terminal state (`succeeded` / `failed`).
 *   3. On success we wipe the React Query cache and hard-refresh so
 *      every cached query (sidebar chats, ticket list, ROI dashboard,
 *      Knowledge Hub, etc.) re-fetches against the freshly seeded
 *      state.
 */
export function DemoResetCard() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "running"; jobId: string }
    | { kind: "done"; completedAt: string }
    | { kind: "timeout"; jobId: string }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  // Independent state for the "Seed demo chats" action. Kept separate
  // from the reset state machine above so the two buttons never clobber
  // each other's status text.
  const [seedState, setSeedState] = useState<
    | { kind: "idle" }
    | { kind: "running" }
    | { kind: "done"; seededChatCount: number }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  // Track whether the component is mounted so the async poll loop
  // doesn't setState after unmount (silent React warning + memory
  // leak). Ref instead of state so the loop's closure sees the latest
  // value without re-running the effect.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  async function pollUntilTerminal(jobId: string): Promise<void> {
    const startedAt = Date.now();
    while (mountedRef.current) {
      if (Date.now() - startedAt > DEMO_RESET_POLL_TIMEOUT_MS) {
        if (mountedRef.current) {
          setState({ kind: "timeout", jobId });
        }
        return;
      }
      try {
        const status = await getDemoResetJobStatus(jobId);
        if (!mountedRef.current) return;
        if (status.status === "succeeded") {
          dispatch(setDemoPersona(null));
          queryClient.cancelQueries();
          queryClient.removeQueries();
          setState({
            kind: "done",
            completedAt: status.completedAt ?? new Date().toISOString(),
          });
          setTimeout(() => {
            window.location.reload();
          }, 400);
          return;
        }
        if (status.status === "failed") {
          setState({
            kind: "error",
            message:
              status.errorMessage ??
              "The reset failed without an error message. Try again.",
          });
          return;
        }
      } catch (pollError) {
        // Transient poll error (network blip, lambda cold start, etc.)
        // — wait the next interval and try again. We only surface an
        // error to the operator if the *job* lands in `failed` or the
        // overall timeout fires.
        if (!mountedRef.current) return;
        // No state update here; the loop continues.
        // eslint-disable-next-line no-console
        console.warn("Demo reset poll failed; will retry", pollError);
      }
      await new Promise((resolve) =>
        setTimeout(resolve, DEMO_RESET_POLL_INTERVAL_MS),
      );
    }
  }

  async function onReset() {
    if (
      !window.confirm(
        "This wipes everything you've changed in the demo and puts it back to " +
          "the original Vitalora content (projects, comments, ROI numbers, " +
          "Slack messages, uploaded files). Real customer data is not touched. " +
          "Continue?",
      )
    ) {
      return;
    }
    try {
      const queued = await resetDemoEnvironment();
      setState({ kind: "running", jobId: queued.jobId });
      await pollUntilTerminal(queued.jobId);
    } catch (e) {
      setState({
        kind: "error",
        message: await errorMessage(e),
      });
    }
  }

  // Second action: repopulate the canonical Ask Puntt demo chats. Reset
  // wipes them and never reseeds, so this is the only way to get the
  // previous-artifact navigation demo back. Idempotent on the backend.
  async function onSeedChats() {
    setSeedState({ kind: "running" });
    try {
      const res = await seedDemoChats();
      // Refresh every Ask Puntt chat list (sidebar + chats route) so the
      // freshly seeded chats appear without a hard reload.
      await queryClient.invalidateQueries({ queryKey: askPunttKeys.all });
      if (!mountedRef.current) return;
      setSeedState({ kind: "done", seededChatCount: res.seededChatCount });
    } catch (e) {
      if (!mountedRef.current) return;
      setSeedState({ kind: "error", message: await errorMessage(e) });
    }
  }

  return (
    <Card data-testid="settings-card-demo">
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Heading as="h2" size="3" weight="bold">
            Reset Demo
          </Heading>
          <Text size="2" color="gray">
            Puts the entire Vitalora demo back to its original state: projects,
            comments, briefs, claims, activity feed, and Slack messages. Use
            this between demos to clear out anything you changed. Real customer
            data is never touched. The wipe runs in the background and usually
            finishes within a minute.
          </Text>
        </Flex>
        <Flex align="center" gap="3">
          <Button
            data-testid="settings-demo-reset-button"
            aria-label="Reset Demo"
            onClick={onReset}
            disabled={state.kind === "running"}
          >
            {state.kind === "running" ? "Resetting…" : "Reset Demo"}
          </Button>
          {state.kind === "running" ? (
            <Text
              size="2"
              color="gray"
              data-testid="settings-demo-reset-running"
            >
              Working in the background. This page will reload when it&apos;s
              done.
            </Text>
          ) : null}
          {state.kind === "done" ? (
            <Text size="2" color="green" data-testid="settings-demo-reset-done">
              Reset finished at{" "}
              {new Date(state.completedAt).toLocaleTimeString()}.
            </Text>
          ) : null}
          {state.kind === "timeout" ? (
            <Text
              size="2"
              color="orange"
              data-testid="settings-demo-reset-timeout"
            >
              Still running after 5 minutes. Refresh in a moment to see the
              result.
            </Text>
          ) : null}
          {state.kind === "error" ? (
            <Text size="2" color="red" data-testid="settings-demo-reset-error">
              {state.message}
            </Text>
          ) : null}
        </Flex>
        <Flex direction="column" gap="1">
          <Heading as="h3" size="2" weight="bold">
            Seed demo chats
          </Heading>
          <Text size="2" color="gray">
            Reset clears every Ask Puntt chat. Use this to add back the example
            previous chats (packaging reviews and brief canvases) so you can
            demo navigating between earlier artifacts. Safe to click more than
            once.
          </Text>
        </Flex>
        <Flex align="center" gap="3">
          <Button
            variant="soft"
            data-testid="settings-demo-seed-chats-button"
            aria-label="Seed demo chats"
            onClick={onSeedChats}
            disabled={seedState.kind === "running"}
          >
            {seedState.kind === "running" ? "Seeding…" : "Seed demo chats"}
          </Button>
          {seedState.kind === "done" ? (
            <Text
              size="2"
              color="green"
              data-testid="settings-demo-seed-chats-done"
            >
              Added {seedState.seededChatCount} demo{" "}
              {seedState.seededChatCount === 1 ? "chat" : "chats"}.
            </Text>
          ) : null}
          {seedState.kind === "error" ? (
            <Text
              size="2"
              color="red"
              data-testid="settings-demo-seed-chats-error"
            >
              {seedState.message}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Card>
  );
}

/**
 * "Bootstrap Vitalora Demo" card — visible to MG superusers on any
 * non-demo enterprise. Solves the chicken-and-egg where the migrator
 * Lambda hasn't been invoked on a stage and Sarah / Priya can't log in
 * because their docs don't exist yet. Idempotent on the backend, so
 * re-clicking on a stage that's already seeded just upserts.
 */
function DemoBootstrapCard() {
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "running" }
    | {
        kind: "done";
        completedAt: string;
        alreadyExisted: boolean;
        enterpriseId: string;
      }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function onBootstrap() {
    if (
      !window.confirm(
        "This sets up the Vitalora demo on this environment. It creates the " +
          "demo company, the six demo users (Sarah, Priya, James, Tom, Mia, " +
          "Alex), example projects, the knowledge base, integrations, and the " +
          "ROI dashboard. Safe to click more than once. Continue?",
      )
    ) {
      return;
    }
    setState({ kind: "running" });
    try {
      const result = await bootstrapDemoEnvironment();
      setState({
        kind: "done",
        completedAt: result.completedAt,
        alreadyExisted: result.alreadyExisted,
        enterpriseId: result.enterpriseId,
      });
    } catch (e) {
      setState({
        kind: "error",
        message: await errorMessage(e),
      });
    }
  }

  return (
    <Card data-testid="settings-card-demo-bootstrap">
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Heading as="h2" size="3" weight="bold">
            Set up the Vitalora demo
          </Heading>
          <Text size="2" color="gray">
            Use this if <code>sarah.chen@vitalora.demo.puntt.ai</code>{" "}
            can&apos;t log in on this environment yet. It creates the demo
            company and demo users from scratch. Safe to click more than once.
          </Text>
        </Flex>
        <Flex align="center" gap="3">
          <Button
            data-testid="settings-demo-bootstrap-button"
            onClick={onBootstrap}
            disabled={state.kind === "running"}
          >
            {state.kind === "running" ? "Setting up…" : "Set up Vitalora demo"}
          </Button>
          {state.kind === "done" ? (
            <Text size="2" color="green">
              {state.alreadyExisted
                ? `Refreshed at ${new Date(state.completedAt).toLocaleTimeString()}.`
                : `Vitalora demo ready at ${new Date(state.completedAt).toLocaleTimeString()}. You can now log in as sarah.chen@vitalora.demo.puntt.ai.`}
            </Text>
          ) : null}
          {state.kind === "error" ? (
            <Text size="2" color="red">
              {state.message}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Card>
  );
}
